import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Bell, X, Check, Heart, Star, Trophy, Gift, Zap,
  MessageCircle, User, Camera, Settings, HelpCircle,
  AlertCircle, CheckCircle, Info, Lightbulb, Target,
  Sparkles, Coffee, Music, Activity, TrendingUp
} from 'lucide-react';

// Notification Context for global state management
const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [contextualHelp, setContextualHelp] = useState(null);
  const [celebrationQueue, setCelebrationQueue] = useState([]);

  const addNotification = (notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      timestamp: new Date(),
      read: false,
      ...notification
    };
    
    setNotifications(prev => [newNotification, ...prev.slice(0, 49)]); // Keep last 50
    
    // Auto-remove after duration
    if (notification.duration && notification.duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, notification.duration);
    }
    
    return id;
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const markAsRead = (id) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  };

  const showContextualHelp = (helpData) => {
    setContextualHelp(helpData);
  };

  const hideContextualHelp = () => {
    setContextualHelp(null);
  };

  const triggerCelebration = (celebration) => {
    const id = Date.now();
    setCelebrationQueue(prev => [...prev, { ...celebration, id }]);
    
    // Auto-remove after animation
    setTimeout(() => {
      setCelebrationQueue(prev => prev.filter(c => c.id !== id));
    }, celebration.duration || 3000);
  };

  return (
    <NotificationContext.Provider value={{
      notifications,
      contextualHelp,
      celebrationQueue,
      addNotification,
      removeNotification,
      markAsRead,
      showContextualHelp,
      hideContextualHelp,
      triggerCelebration
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Toast Notification Component
export const SmartToast = ({ notification, onClose, onAction }) => {
  const [isVisible, setIsVisible] = useState(true);
  
  const icons = {
    success: <CheckCircle className="w-5 h-5 text-green-500" />,
    error: <AlertCircle className="w-5 h-5 text-red-500" />,
    warning: <AlertCircle className="w-5 h-5 text-yellow-500" />,
    info: <Info className="w-5 h-5 text-blue-500" />,
    match: <Heart className="w-5 h-5 text-rose-500" />,
    message: <MessageCircle className="w-5 h-5 text-purple-500" />
  };

  const backgrounds = {
    success: 'bg-green-50 border-green-200',
    error: 'bg-red-50 border-red-200',
    warning: 'bg-yellow-50 border-yellow-200',
    info: 'bg-blue-50 border-blue-200',
    match: 'bg-rose-50 border-rose-200',
    message: 'bg-purple-50 border-purple-200'
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), 200);
  };

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 300, scale: 0.8 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 300, scale: 0.8 }}
      className={`max-w-sm w-full ${backgrounds[notification.type]} shadow-lg rounded-lg p-4 border ${notification.className || ''}`}
    >
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          {icons[notification.type] || icons.info}
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-gray-900">
            {notification.title}
          </h4>
          {notification.message && (
            <p className="mt-1 text-sm text-gray-600">
              {notification.message}
            </p>
          )}
          
          {notification.action && (
            <div className="mt-3 flex space-x-2">
              <Button
                size="sm"
                onClick={() => {
                  onAction(notification.action);
                  handleClose();
                }}
                className="text-xs px-3 py-1 bg-rose-500 hover:bg-rose-600"
              >
                {notification.action.label}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleClose}
                className="text-xs px-3 py-1"
              >
                Dismiss
              </Button>
            </div>
          )}
        </div>
        
        <Button
          size="sm"
          variant="ghost"
          onClick={handleClose}
          className="flex-shrink-0 p-1 h-auto"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </motion.div>
  );
};

// Success Celebration Component
export const SuccessCelebration = ({ celebration }) => {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    // Generate celebration particles
    const newParticles = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 0.5,
      duration: 2 + Math.random() * 2
    }));
    setParticles(newParticles);
  }, []);

  const celebrationIcons = {
    match: <Heart className="w-8 h-8 text-rose-500" />,
    level_up: <Trophy className="w-8 h-8 text-yellow-500" />,
    streak: <Zap className="w-8 h-8 text-orange-500" />,
    achievement: <Star className="w-8 h-8 text-purple-500" />,
    gift: <Gift className="w-8 h-8 text-green-500" />
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.5 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm"
    >
      {/* Celebration Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map(particle => (
          <motion.div
            key={particle.id}
            initial={{ 
              opacity: 0, 
              scale: 0, 
              x: `${particle.x}vw`, 
              y: `${particle.y}vh` 
            }}
            animate={{ 
              opacity: [0, 1, 0], 
              scale: [0, 1, 0.5], 
              y: [`${particle.y}vh`, `${particle.y - 20}vh`]
            }}
            transition={{ 
              duration: particle.duration, 
              delay: particle.delay,
              ease: "easeOut"
            }}
            className="absolute w-2 h-2 bg-rose-400 rounded-full"
          />
        ))}
      </div>

      {/* Main Celebration Card */}
      <motion.div
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -50, opacity: 0 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
        className="bg-white rounded-2xl p-8 shadow-2xl text-center max-w-sm mx-4"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: [0, 1.2, 1] }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mb-4 flex justify-center"
        >
          {celebrationIcons[celebration.type] || celebrationIcons.achievement}
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-2xl font-bold text-gray-800 mb-2"
        >
          {celebration.title}
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-gray-600 mb-6"
        >
          {celebration.message}
        </motion.p>

        {celebration.reward && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7 }}
            className="bg-gradient-to-r from-rose-100 to-pink-100 rounded-lg p-4 mb-6"
          >
            <div className="flex items-center justify-center space-x-2">
              <Gift className="w-5 h-5 text-rose-500" />
              <span className="text-sm font-medium text-rose-700">
                {celebration.reward}
              </span>
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <Button 
            onClick={celebration.onClose}
            className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600"
          >
            Continue
          </Button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

// Contextual Help Component
export const ContextualHelp = ({ helpData, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const nextStep = () => {
    if (currentStep < helpData.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const currentStepData = helpData.steps[currentStep];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
    >
      <div className="min-h-screen flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
        >
          {/* Progress Indicator */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex space-x-2">
              {helpData.steps.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full ${
                    index <= currentStep ? 'bg-rose-500' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
            <Badge variant="outline" className="text-xs">
              {currentStep + 1} of {helpData.steps.length}
            </Badge>
          </div>

          {/* Step Content */}
          <div className="text-center mb-6">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="mb-4 flex justify-center">
                <div className="w-16 h-16 bg-gradient-to-br from-rose-100 to-pink-100 rounded-full flex items-center justify-center">
                  {currentStepData.icon || <HelpCircle className="w-8 h-8 text-rose-500" />}
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-800 mb-3">
                {currentStepData.title}
              </h3>
              
              <p className="text-gray-600 leading-relaxed">
                {currentStepData.description}
              </p>

              {currentStepData.tip && (
                <div className="mt-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                  <div className="flex items-start">
                    <Lightbulb className="w-4 h-4 text-yellow-500 mt-0.5 mr-2" />
                    <p className="text-sm text-yellow-700">{currentStepData.tip}</p>
                  </div>
                </div>
              )}
            </motion.div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 0}
              className="flex items-center space-x-2"
            >
              <span>Previous</span>
            </Button>
            
            <div className="flex space-x-2">
              <Button
                variant="ghost"
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                Skip
              </Button>
              
              <Button
                onClick={nextStep}
                className="bg-rose-500 hover:bg-rose-600 flex items-center space-x-2"
              >
                <span>
                  {currentStep === helpData.steps.length - 1 ? 'Got it!' : 'Next'}
                </span>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

// Notification Center
export const NotificationCenter = ({ isOpen, onClose }) => {
  const { notifications, markAsRead, removeNotification } = useNotifications();
  
  const unreadCount = notifications.filter(n => !n.read).length;
  const todayNotifications = notifications.filter(n => {
    const today = new Date();
    const notificationDate = new Date(n.timestamp);
    return notificationDate.toDateString() === today.toDateString();
  });
  
  const olderNotifications = notifications.filter(n => {
    const today = new Date();
    const notificationDate = new Date(n.timestamp);
    return notificationDate.toDateString() !== today.toDateString();
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Notifications</h2>
                {unreadCount > 0 && (
                  <Badge className="bg-rose-500 text-white text-xs">
                    {unreadCount} new
                  </Badge>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                  <Bell className="w-12 h-12 mb-4 opacity-50" />
                  <p>No notifications yet</p>
                  <p className="text-sm">You'll see updates here</p>
                </div>
              ) : (
                <div className="p-4 space-y-6">
                  {/* Today */}
                  {todayNotifications.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 mb-3">Today</h3>
                      <div className="space-y-2">
                        {todayNotifications.map(notification => (
                          <NotificationItem
                            key={notification.id}
                            notification={notification}
                            onRead={() => markAsRead(notification.id)}
                            onRemove={() => removeNotification(notification.id)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Older */}
                  {olderNotifications.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 mb-3">Earlier</h3>
                      <div className="space-y-2">
                        {olderNotifications.slice(0, 10).map(notification => (
                          <NotificationItem
                            key={notification.id}
                            notification={notification}
                            onRead={() => markAsRead(notification.id)}
                            onRemove={() => removeNotification(notification.id)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Individual Notification Item
const NotificationItem = ({ notification, onRead, onRemove }) => {
  const icons = {
    match: <Heart className="w-4 h-4 text-rose-500" />,
    message: <MessageCircle className="w-4 h-4 text-blue-500" />,
    achievement: <Trophy className="w-4 h-4 text-yellow-500" />,
    system: <Info className="w-4 h-4 text-gray-500" />
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-3 rounded-lg border cursor-pointer transition-all ${
        notification.read 
          ? 'bg-gray-50 border-gray-200' 
          : 'bg-white border-rose-200 shadow-sm'
      }`}
      onClick={() => !notification.read && onRead()}
    >
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 mt-1">
          {icons[notification.type] || icons.system}
        </div>
        
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${
            notification.read ? 'text-gray-600' : 'text-gray-900'
          }`}>
            {notification.title}
          </p>
          
          {notification.message && (
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
              {notification.message}
            </p>
          )}
          
          <p className="text-xs text-gray-400 mt-2">
            {new Date(notification.timestamp).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
        </div>

        {!notification.read && (
          <div className="w-2 h-2 bg-rose-500 rounded-full flex-shrink-0 mt-2"></div>
        )}
      </div>
    </motion.div>
  );
};

// Global Toast Container
export const GlobalToastContainer = () => {
  const { notifications, removeNotification } = useNotifications();
  
  const toastNotifications = notifications.filter(n => n.isToast && !n.read).slice(0, 3);

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      <AnimatePresence>
        {toastNotifications.map(notification => (
          <SmartToast
            key={notification.id}
            notification={notification}
            onClose={() => removeNotification(notification.id)}
            onAction={(action) => action.handler()}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

// Global Celebration Container  
export const GlobalCelebrationContainer = () => {
  const { celebrationQueue, triggerCelebration } = useNotifications();

  return (
    <AnimatePresence>
      {celebrationQueue.map(celebration => (
        <SuccessCelebration
          key={celebration.id}
          celebration={{
            ...celebration,
            onClose: () => {
              // Remove from queue is handled by timeout in triggerCelebration
            }
          }}
        />
      ))}
    </AnimatePresence>
  );
};

export default {
  NotificationProvider,
  useNotifications,
  SmartToast,
  SuccessCelebration,
  ContextualHelp,
  NotificationCenter,
  GlobalToastContainer,
  GlobalCelebrationContainer
};